import React, { useEffect, useState } from 'react';
import API_CONFIG from '../../config/api';
import authService, { AuthResponse, SocialAuthConfig } from '../../services/authService';
import {
  loadFacebookSdk,
  loadGoogleSdk,
  socialOauthStartUrl,
} from '../../utils/socialAuth';
import { trackEvent } from '../../utils/analytics';
import './SocialLoginButtons.css';

interface SocialLoginButtonsProps {
  onSuccess: (response: AuthResponse) => void | Promise<void>;
  onError?: (message: string) => void;
  disabled?: boolean;
}

const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
  onSuccess,
  onError,
  disabled = false,
}) => {
  const [config, setConfig] = useState<SocialAuthConfig | null>(null);
  const [busyProvider, setBusyProvider] = useState<'google' | 'facebook' | null>(null);

  useEffect(() => {
    let cancelled = false;
    authService
      .getSocialAuthConfig()
      .then((next) => {
        if (!cancelled) {
          setConfig(next);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setConfig({ success: false, googleEnabled: false, facebookEnabled: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!config || (!config.googleEnabled && !config.facebookEnabled)) {
    return null;
  }

  const redirectToOauth = (provider: 'google' | 'facebook') => {
    const startPath = socialOauthStartUrl(provider);
    window.location.assign(`${API_CONFIG.baseURL}${startPath}`);
  };

  const finishLogin = async (provider: 'google' | 'facebook', credential: string) => {
    setBusyProvider(provider);
    try {
      const response = await authService.socialLogin(provider, credential);
      if (response.success) {
        trackEvent('sign_in', { method: provider });
        await onSuccess(response);
      } else {
        onError?.(response.message || 'Social sign-in failed');
      }
    } catch (err: any) {
      onError?.(err.response?.data?.message || 'Social sign-in failed. Please try email or OTP.');
    } finally {
      setBusyProvider(null);
    }
  };

  const handleGoogle = async () => {
    if (disabled || busyProvider || !config.googleClientId) {
      return;
    }
    setBusyProvider('google');
    try {
      await loadGoogleSdk();
      if (!window.google?.accounts?.oauth2) {
        redirectToOauth('google');
        return;
      }
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: config.googleClientId,
        scope: 'openid email profile',
        callback: (resp) => {
          if (resp.error === 'popup_closed_by_user' || resp.error === 'access_denied') {
            setBusyProvider(null);
            return;
          }
          if (resp.error || !resp.access_token) {
            redirectToOauth('google');
            return;
          }
          finishLogin('google', resp.access_token);
        },
      });
      client.requestAccessToken({ prompt: 'select_account' });
      setBusyProvider(null);
    } catch (err) {
      redirectToOauth('google');
    }
  };

  const handleFacebook = async () => {
    if (disabled || busyProvider || !config.facebookAppId) {
      return;
    }
    setBusyProvider('facebook');
    try {
      await loadFacebookSdk(config.facebookAppId);
      if (!window.FB) {
        redirectToOauth('facebook');
        return;
      }
      window.FB.login(
        (resp) => {
          if (resp.authResponse?.accessToken) {
            finishLogin('facebook', resp.authResponse.accessToken);
            return;
          }
          setBusyProvider(null);
        },
        { scope: 'email,public_profile' }
      );
    } catch (err) {
      redirectToOauth('facebook');
    }
  };

  const busy = disabled || !!busyProvider;

  return (
    <div className="social-login">
      {config.googleEnabled && (
        <button
          type="button"
          className="social-login-btn social-login-btn--google"
          onClick={handleGoogle}
          disabled={busy}
        >
          <span className="social-login-icon" aria-hidden="true">
            <svg viewBox="0 0 18 18" width="18" height="18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
              <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z" />
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" />
            </svg>
          </span>
          Continue with Google
        </button>
      )}
      {config.facebookEnabled && (
        <button
          type="button"
          className="social-login-btn social-login-btn--facebook"
          onClick={handleFacebook}
          disabled={busy}
        >
          <span className="social-login-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M22.675 0H1.325C.593 0 0 .593 0 1.326v21.348C0 23.406.593 24 1.325 24H12.82v-9.294H9.692V11.01h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24h-1.918c-1.504 0-1.795.715-1.795 1.763v2.24h3.587l-.467 3.696h-3.12V24h6.116C23.406 24 24 23.406 24 22.674V1.326C24 .593 23.406 0 22.675 0z" />
            </svg>
          </span>
          Continue with Facebook
        </button>
      )}
      <div className="social-login-divider" role="separator">
        <span>or continue with email / OTP</span>
      </div>
    </div>
  );
};

export default SocialLoginButtons;
